import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { language, ti } = useLanguage();
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);
  const intent = params.get("intent") === "signin" ? "signin" : "signup";
  const invitationToken = params.get("invitation") || params.get("invite") || params.get("invitation_token") || "";
  const magicError = params.get("magic") === "invalid";

  useEffect(() => {
    api.get("/auth/me").then(() => navigate("/dashboard")).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (magicError) {
      toast.error(ti(
        "That sign-in link is invalid or has expired. Please request a new one.",
        "Ese enlace de acceso no es válido o ha caducado. Solicita uno nuevo.",
        "Dieser Anmelde-Link ist ungültig oder abgelaufen. Bitte fordere einen neuen an."
      ));
    }
    const g = params.get("google");
    if (g === "failed") {
      toast.error(ti("Google sign-in didn't complete. Please try again.",
        "El inicio de sesión con Google no se completó. Inténtalo de nuevo.",
        "Die Google-Anmeldung wurde nicht abgeschlossen. Bitte versuche es erneut."));
    } else if (g === "unconfigured") {
      toast.error(ti("Google sign-in isn't available right now. Please use email.",
        "El inicio de sesión con Google no está disponible ahora. Usa el correo.",
        "Google-Anmeldung ist gerade nicht verfügbar. Bitte nutze die E-Mail."));
    }
  }, [magicError, ti]);

  const handleGoogle = async () => {
    setLoading(true);
    // Studio Plaz-OWNED Google OAuth (backend Authorization-Code flow). The consent screen
    // reads "Studio Plaz" — no emergentagent.com. Journey/invitation are carried server-side.
    const backend = process.env.REACT_APP_BACKEND_URL;
    const qs = new URLSearchParams({ journey: intent, redirect: "/dashboard" });
    if (invitationToken) qs.set("invitation_token", invitationToken);
    window.location.href = `${backend}/api/auth/google/start?${qs.toString()}`;
  };

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!validEmail || sending) return;
    setSending(true);
    try {
      await api.post("/auth/magic-link/request", {
        email: email.trim(),
        language,
        redirect: "/dashboard",
        journey: intent,
        invitation_token: invitationToken || undefined,
      });
      setSent(true);
    } catch (err) {
      toast.error(ti(
        "Something went wrong. Please try again.",
        "Algo salió mal. Inténtalo de nuevo.",
        "Etwas ist schiefgelaufen. Bitte versuche es erneut."
      ));
    } finally {
      setSending(false);
    }
  };

  const title = intent === "signin"
    ? ti("Sign in to Studio Plaz", "Inicia sesión en Studio Plaz", "Bei Studio Plaz anmelden")
    : ti("Start free with Studio Plaz", "Comienza gratis con Studio Plaz", "Kostenlos mit Studio Plaz starten");
  const subtitle = intent === "signin"
    ? ti("Welcome back — choose how to continue.", "Bienvenido de nuevo — elige cómo continuar.", "Willkommen zurück — wähle, wie du fortfahren möchtest.")
    : ti("No credit card required.", "Sin tarjeta de crédito.", "Keine Kreditkarte erforderlich.");

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/studio-plaz-logo-white.png" alt="Studio Plaz" className="h-14 w-auto mx-auto mb-6" />
          <h1 className="text-xl font-semibold text-white mb-1" style={{ color: "#ffffff" }} data-testid="login-title">{title}</h1>
          <p className="text-sm text-[var(--app-text-muted)]">{subtitle}</p>
        </div>

        <div className="ios-card-elevated p-7 space-y-4" data-testid="login-card">
          {sent ? (
            <div className="text-center py-4" data-testid="magic-sent-state">
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-[#0070FF]/15 flex items-center justify-center">
                <svg className="w-6 h-6 text-[#0070FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-white font-semibold mb-1">
                {ti("Check your inbox", "Revisa tu correo", "Prüfe dein Postfach")}
              </h2>
              <p className="text-sm text-[var(--app-text-muted)] mb-5">
                {ti(
                  `We sent a sign-in link to ${email.trim()}. It works once and expires in 15 minutes.`,
                  `Enviamos un enlace de acceso a ${email.trim()}. Es de un solo uso y caduca en 15 minutos.`,
                  `Wir haben einen Anmelde-Link an ${email.trim()} gesendet. Er ist einmalig gültig und läuft in 15 Minuten ab.`
                )}
              </p>
              <button
                onClick={() => { setSent(false); }}
                data-testid="magic-use-different-email-btn"
                className="text-sm text-[#0070FF] hover:underline"
              >
                {ti("Use a different email", "Usar otro correo", "Andere E-Mail verwenden")}
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleMagicLink} className="space-y-3" data-testid="magic-link-form">
                <label className="block text-xs font-medium text-[var(--app-text-muted)] mb-1">
                  {ti("Email address", "Correo electrónico", "E-Mail-Adresse")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={ti("you@company.com", "tu@empresa.com", "du@firma.com")}
                  autoComplete="email"
                  data-testid="magic-email-input"
                  className="w-full bg-[var(--app-surface)] border border-[var(--app-border)] text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0070FF] transition-colors"
                />
                <button
                  type="submit"
                  disabled={!validEmail || sending}
                  data-testid="magic-link-submit-btn"
                  className="w-full flex items-center justify-center gap-2 bg-[#0070FF] hover:bg-[#0060DD] text-white font-semibold py-3.5 px-6 rounded-xl transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <div className="w-5 h-5 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    ti("Continue with email", "Continuar con correo", "Mit E-Mail fortfahren")
                  )}
                </button>
              </form>

              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-[var(--app-border)]" />
                <span className="text-xs text-[var(--app-text-muted)] uppercase tracking-wider">
                  {ti("or", "o", "oder")}
                </span>
                <div className="flex-1 h-px bg-[var(--app-border)]" />
              </div>

              <button
                onClick={handleGoogle}
                disabled={loading}
                data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-3 bg-white text-[#19191D] border border-[var(--app-border)] font-semibold py-3.5 px-6 rounded-xl hover:bg-white/90 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[var(--app-text-muted)] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {ti("Continue with Google", "Continuar con Google", "Mit Google fortfahren")}
                  </>
                )}
              </button>
            </>
          )}
        </div>

        <p className="text-center text-xs text-[var(--app-text-muted)] mt-6" data-testid="login-intent-switch">
          {intent === "signin" ? (
            <>
              {ti("New to Studio Plaz?", "¿Nuevo en Studio Plaz?", "Neu bei Studio Plaz?")}{" "}
              <a href="/login?intent=signup" className="text-[#0070FF] hover:underline">
                {ti("Start free", "Comienza gratis", "Kostenlos starten")}
              </a>
            </>
          ) : (
            <>
              {ti("Already have an account?", "¿Ya tienes una cuenta?", "Hast du bereits ein Konto?")}{" "}
              <a href="/login?intent=signin" className="text-[#0070FF] hover:underline">
                {ti("Sign in", "Iniciar sesión", "Anmelden")}
              </a>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
